import { useMemo, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Badge,
  V2Button,
  V2EmptyState,
  V2FormGrid,
  V2FormField,
  Icon,
  LoadingState,
  StatePanel,
  V2StatusStrip,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { formatRialFa, parseRialInput } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type {
  AccountListResponse,
  DimensionRuleListResponse,
  DimensionValue,
  FiscalPeriodListResponse,
  JournalEntry,
  JournalListResponse,
  JournalTemplateListResponse,
} from '../../lib/types/finance.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import {
  JOURNAL_STATUS_TEXT,
  JOURNAL_STATUS_TONE,
  JournalDocumentCard,
  JournalStatusBadge,
} from './JournalDocumentCard.js';
import { useFinanceBook } from './useFinanceBook.js';
import type { AccountingBookListResponse } from '../../lib/types/finance.js';

interface DraftLine {
  readonly accountId: string;
  readonly description: string;
  readonly debitRial: string;
  readonly creditRial: string;
  /** dimensionId → dimensionValueId (floating detail levels). */
  readonly dimensions: Readonly<Record<string, string>>;
}

const EMPTY_LINE: DraftLine = {
  accountId: '',
  description: '',
  debitRial: '',
  creditRial: '',
  dimensions: {},
};

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Finance.dc.html §02 JOURNAL LIST + §04 JOURNAL CREATE —
 *   filter bar, table with an always-visible debit/credit-total footer,
 *   and a create form with a live balance check that disables submission
 *   until the document balances.
 * archetype: ENTITY_LIST + ENTITY_CREATE_EDIT
 * status: KIT_CONFORMANT — the board's create form has both "ارسال برای
 *   بررسی" (create + immediately submit) and "ذخیرهٔ پیش‌نویس"; only the
 *   real single-step `POST .../finance/journals` (draft) exists here —
 *   review/approval/post/reverse are separate real actions on the detail
 *   page, so this form only offers "ثبت پیش‌نویس".
 * state reference: ready · empty · permission_denied · network_error
 *
 * 2026-10-08 (Finance V2 pass): the list is now the reference's master/detail
 * — left «اسناد حسابداری» card (number, status badge, description, amount,
 * date; selecting a row shows it) beside the shared «سند» card
 * (JournalDocumentCard.tsx: meta strip, lines grid, «جمع سند · n», balance
 * indicator, actions, attachments). Deep link /journals/:id is unchanged
 * (JournalDetailPage renders the same card). The create form moved behind
 * the «سند جدید» button (nothing removed). Status badges follow the V2
 * lifecycle («چرخهٔ وضعیت سند» legend below the grid). Omitted — no data:
 * the period in the list title («اسناد <دوره>»: the list endpoint has no
 * period filter/field), ثبت‌کننده/تأییدکننده, «چاپ سند» (no endpoint).
 * UI_REFERENCE_SOURCE (v2 file): Sakhtar Cloud ERP v2.dc.html /t/kimia/finance
 * journals tab · status: PARTIAL_REAL.
 *
 * Fixed 2026-09-26 (form-hygiene pass): the live create-form balance check
 * summed each line's `debitRial`/`creditRial` as `Number()`, and the list's
 * per-row and footer totals did the same on server-returned
 * `debitTotal`/`creditTotal` — the exact FE-P0 FE-HYGIENE-1
 * `Number()`-on-a-Rial-digit-string anti-pattern already ruled out
 * elsewhere in Finance (see `TreasuryPage.tsx`). Now accumulates as
 * `bigint`, converting once at the display boundary via `formatRialFa`.
 * The per-line debit/credit inputs also gained `inputMode="numeric"` and
 * digit-only `onChange` filtering, matching every sibling money input in
 * this route set — they previously accepted a literal decimal point or
 * letters directly into a Rial field.
 */
export function JournalsPage() {
  const { unit, label } = useAmountDisplay();
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [businessDate, setBusinessDate] = useState('');
  const [fiscalPeriodId, setFiscalPeriodId] = useState('');
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const bookState = useFinanceBook();
  // «همه دفاتر» lists every book; otherwise the chosen book (the statutory book by default).
  const selectedBookId = bookState.isAll ? null : bookState.bookId;
  const [targetBookId, setTargetBookId] = useState<string>('');

  const books = useApiGet<AccountingBookListResponse>(
    slug ? `${base}/api/v1/finance/books` : null,
    [slug],
  );

  const journalUrl =
    slug && bookState.ready
      ? `${base}/api/v1/accounting/journal-entries${selectedBookId ? `?bookId=${selectedBookId}` : ''}`
      : null;
  const journals = useApiGet<JournalListResponse>(journalUrl, [slug, refreshKey, selectedBookId]);
  const fiscalPeriods = useApiGet<FiscalPeriodListResponse>(
    slug ? `${base}/api/v1/accounting/fiscal-periods` : null,
    [slug],
  );
  const accounts = useApiGet<AccountListResponse>(slug ? `${base}/api/v1/finance/accounts` : null, [
    slug,
  ]);
  const rules = useApiGet<DimensionRuleListResponse>(
    slug ? `${base}/api/v1/finance/dimension-rules?activeOnly=true` : null,
    [slug],
  );
  const dimensionValues = useApiGet<{ readonly items: readonly DimensionValue[] }>(
    slug ? `${base}/api/v1/master-data/dimension-values?status=active&limit=200` : null,
    [slug],
  );
  const [templateRefresh, setTemplateRefresh] = useState(0);
  const templates = useApiGet<JournalTemplateListResponse>(
    slug ? `${base}/api/v1/finance/journal-templates` : null,
    [slug, templateRefresh],
  );
  const [templateCode, setTemplateCode] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateKeepAmounts, setTemplateKeepAmounts] = useState(false);
  const [templateMessage, setTemplateMessage] = useState<string | null>(null);

  const rulesByAccount = useMemo(() => {
    const map = new Map<string, DimensionRuleListResponse['items'][number][]>();
    if (rules.status === 'ready') {
      for (const rule of rules.data.items) {
        map.set(rule.accountId, [...(map.get(rule.accountId) ?? []), rule]);
      }
    }
    return map;
  }, [rules]);

  function applyTemplate(templateId: string) {
    if (templates.status !== 'ready') return;
    const template = templates.data.items.find((t) => t.journalTemplateId === templateId);
    if (!template) return;
    setLines(
      template.lines.map((l) => ({
        ...EMPTY_LINE,
        accountId: l.accountId,
        description: l.description ?? '',
        debitRial: l.side === 'debit' ? (l.amountRial ?? '') : '',
        creditRial: l.side === 'credit' ? (l.amountRial ?? '') : '',
      })),
    );
    if (!description.trim()) setDescription(template.name);
  }

  async function saveAsTemplate() {
    setTemplateMessage(null);
    const usable = lines.filter((l) => l.accountId && (l.debitRial || l.creditRial));
    if (!templateCode.trim() || !templateName.trim() || usable.length < 2) {
      setTemplateMessage('برای ذخیرهٔ الگو، کد، نام و حداقل دو سطر با حساب و مبلغ لازم است.');
      return;
    }
    try {
      await apiPost(`${base}/api/v1/finance/journal-templates`, {
        code: templateCode.trim(),
        name: templateName.trim(),
        lines: usable.map((l) => ({
          accountId: l.accountId,
          side: l.debitRial ? 'debit' : 'credit',
          ...(templateKeepAmounts ? { amountRial: l.debitRial || l.creditRial } : {}),
          ...(l.description.trim() ? { description: l.description.trim() } : {}),
        })),
      });
      setTemplateCode('');
      setTemplateName('');
      setTemplateMessage('الگو ذخیره شد.');
      setTemplateRefresh((k) => k + 1);
    } catch (err) {
      setTemplateMessage(bannerMessageFrom(err, 'ذخیرهٔ الگو ناموفق بود — کد تکراری است؟'));
    }
  }

  // BigInt-accumulated per FE-P0 FE-HYGIENE-1 (same rule as TreasuryPage.tsx's
  // allocation total) — summing multiple Rial line amounts as `Number()`
  // would silently lose precision on large digit strings; digits are
  // validated on input (onChange strips non-digits), so a bare `BigInt()`
  // parse is safe here.
  const totals = useMemo(() => {
    const debit = lines.reduce((sum, l) => sum + (l.debitRial ? BigInt(l.debitRial) : 0n), 0n);
    const credit = lines.reduce((sum, l) => sum + (l.creditRial ? BigInt(l.creditRial) : 0n), 0n);
    return { debit, credit, diff: debit - credit, balanced: debit > 0n && debit === credit };
  }, [lines]);

  const filteredRows: JournalEntry[] = useMemo(() => {
    if (journals.status !== 'ready') return [];
    const q = query.trim().toLowerCase();
    return journals.data.items.filter((j) => {
      if (statusFilter !== 'all' && j.status !== statusFilter) return false;
      if (!q) return true;
      return (
        j.provisionalNumber.toLowerCase().includes(q) ||
        (j.officialDocumentNumber ?? '').toLowerCase().includes(q) ||
        (j.description ?? '').toLowerCase().includes(q)
      );
    });
  }, [journals, query, statusFilter]);

  const activeId =
    filteredRows.find((r) => r.journalId === selectedId)?.journalId ??
    filteredRows[0]?.journalId ??
    null;

  if (!slug) return null;

  function updateLine(index: number, patch: Partial<DraftLine>) {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    const period =
      fiscalPeriods.status === 'ready'
        ? fiscalPeriods.data.items.find((p) => p.fiscalPeriodId === fiscalPeriodId)
        : undefined;
    if (!period) {
      setFormError('دوره مالی را انتخاب کنید.');
      return;
    }
    const validLines = lines.filter((l) => l.accountId && (l.debitRial || l.creditRial));
    if (validLines.length < 2) {
      setFormError('حداقل دو سطر با حساب و مبلغ لازم است.');
      return;
    }
    const missingRequired = validLines.some((l) =>
      (rulesByAccount.get(l.accountId) ?? []).some(
        (r) => r.required && !l.dimensions[r.dimensionId],
      ),
    );
    if (missingRequired) {
      setFormError('برای برخی سطرها انتخاب سطح تفصیلی الزامی است.');
      return;
    }
    if (!totals.balanced) {
      setFormError('سند تراز نیست — مجموع بدهکار و بستانکار باید برابر باشد.');
      return;
    }
    setSubmitting(true);
    try {
      await apiPost(
        `${base}/api/v1/finance/journals`,
        {
          businessDate,
          fiscalYearId: period.fiscalYearId,
          fiscalPeriodId: period.fiscalPeriodId,
          description: description.trim() || undefined,
          bookId: targetBookId || undefined,
          sourceModule: 'manual',
          sourceType: 'manual-entry',
          lines: validLines.map((l, i) => ({
            rowNumber: i + 1,
            accountId: l.accountId,
            description: l.description.trim() || undefined,
            debitRial: l.debitRial || undefined,
            creditRial: l.creditRial || undefined,
            dimensions: Object.entries(l.dimensions)
              .filter(([, val]) => Boolean(val))
              .map(([dimensionId, dimensionValueId]) => ({
                dimensionId,
                dimensionValueId,
              })),
          })),
        },
        { headers: { 'idempotency-key': crypto.randomUUID() } },
      );
      setBusinessDate('');
      setFiscalPeriodId('');
      setDescription('');
      setLines([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
      setRefreshKey((k) => k + 1);
      setFormOpen(false);
    } catch {
      setFormError('ثبت سند ناموفق بود — فیلدهای الزامی را بررسی کنید.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {formOpen ? (
        <V2Card>
          <V2CardHeader
            icon="plus"
            title="سند جدید (پیش‌نویس)"
            actions={
              <V2Button type="button" variant="ghost" size="sm" onClick={() => setFormOpen(false)}>
                بستن فرم
              </V2Button>
            }
          />
          <form onSubmit={onCreate} style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="sk-card-body">
              <V2FormGrid>
                <V2FormField label="تاریخ سند" required width="medium">
                  <JalaliDateInput value={businessDate} onChange={setBusinessDate} />
                </V2FormField>
                <V2FormField label="دورهٔ مالی" required width="medium">
                  <select
                    className="sk-select"
                    value={fiscalPeriodId}
                    onChange={(e) => setFiscalPeriodId(e.target.value)}
                  >
                    <option value="">انتخاب دورهٔ مالی</option>
                    {fiscalPeriods.status === 'ready' &&
                      fiscalPeriods.data.items.map((p) => (
                        <option
                          key={p.fiscalPeriodId}
                          value={p.fiscalPeriodId}
                          disabled={p.postingState !== 'open'}
                        >
                          {p.name} {p.postingState !== 'open' ? '· بسته' : ''}
                        </option>
                      ))}
                  </select>
                </V2FormField>
                <V2FormField label="دفتر حسابداری" width="medium">
                  <select
                    className="sk-select"
                    value={targetBookId}
                    onChange={(e) => setTargetBookId(e.target.value)}
                  >
                    <option value="">دفتر قانونی (پیش‌فرض)</option>
                    {books.status === 'ready' &&
                      books.data.items.map((b) => (
                        <option key={b.bookId} value={b.bookId}>
                          {b.name} ({b.code})
                        </option>
                      ))}
                  </select>
                </V2FormField>
                <V2FormField label="شرح سند" width="wide">
                  <input
                    className="sk-input"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </V2FormField>
              </V2FormGrid>
            </div>

            <div className="sk-card-header" style={{ borderTop: '1px solid var(--sk-divider)' }}>
              <span style={{ fontSize: 15, fontWeight: 600 }}>اقلام سند</span>
              {templates.status === 'ready' && templates.data.items.length > 0 ? (
                <select
                  className="sk-select"
                  aria-label="الگوی سند"
                  value=""
                  style={{ width: 200, marginInlineStart: 'auto' }}
                  onChange={(e) => applyTemplate(e.target.value)}
                >
                  <option value="">بارگذاری از الگو…</option>
                  {templates.data.items.map((t) => (
                    <option key={t.journalTemplateId} value={t.journalTemplateId}>
                      {t.code} — {t.name}
                    </option>
                  ))}
                </select>
              ) : null}
              <V2Button
                type="button"
                variant="secondary"
                size="sm"
                icon="plus"
                onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])}
              >
                افزودن ردیف
              </V2Button>
            </div>
            <div className="sk-table-scroll">
              <table className="sk-table">
                <thead>
                  <tr>
                    <th style={{ width: 44 }}>#</th>
                    <th style={{ width: 200 }}>حساب معین</th>
                    <th style={{ width: 220 }}>تفصیلی (شخص / مرکز هزینه / پروژه)</th>
                    <th>شرح ردیف</th>
                    <th style={{ width: 140 }}>بدهکار (ریال)</th>
                    <th style={{ width: 140 }}>بستانکار (ریال)</th>
                    <th style={{ width: 44 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, i) => (
                    <tr key={i}>
                      <td className="sk-num">{i + 1}</td>
                      <td>
                        <select
                          className="sk-select"
                          value={line.accountId}
                          onChange={(e) =>
                            updateLine(i, { accountId: e.target.value, dimensions: {} })
                          }
                        >
                          <option value="">انتخاب معین…</option>
                          {accounts.status === 'ready' &&
                            accounts.data.items
                              .filter((a) => a.isPostable)
                              .map((a) => (
                                <option key={a.accountId} value={a.accountId}>
                                  {a.code} — {a.name}
                                </option>
                              ))}
                        </select>
                      </td>
                      <td>
                        {(rulesByAccount.get(line.accountId) ?? []).length > 0 ? (
                          (rulesByAccount.get(line.accountId) ?? []).map((rule) => (
                            <select
                              key={rule.dimensionId}
                              className="sk-select"
                              aria-label={rule.dimensionName}
                              style={{ marginBottom: 4 }}
                              value={line.dimensions[rule.dimensionId] ?? ''}
                              onChange={(e) =>
                                updateLine(i, {
                                  dimensions: {
                                    ...line.dimensions,
                                    [rule.dimensionId]: e.target.value,
                                  },
                                })
                              }
                            >
                              <option value="">
                                {rule.dimensionName}
                                {rule.required ? ' (الزامی)' : ' (اختیاری)'}
                              </option>
                              {dimensionValues.status === 'ready' &&
                                dimensionValues.data.items
                                  .filter((v) => v.dimensionId === rule.dimensionId)
                                  .map((v) => (
                                    <option key={v.dimensionValueId} value={v.dimensionValueId}>
                                      {v.code} — {v.name}
                                    </option>
                                  ))}
                            </select>
                          ))
                        ) : (
                          <select
                            className="sk-select"
                            value={Object.values(line.dimensions)[0] ?? ''}
                            onChange={(e) => {
                              const val =
                                dimensionValues.status === 'ready'
                                  ? dimensionValues.data.items.find(
                                      (v) => v.dimensionValueId === e.target.value,
                                    )
                                  : null;
                              if (val) {
                                updateLine(i, {
                                  dimensions: { [val.dimensionId]: val.dimensionValueId },
                                });
                              } else {
                                updateLine(i, { dimensions: {} });
                              }
                            }}
                          >
                            <option value="">— بدون تفصیلی / تفصیلی شناور —</option>
                            {dimensionValues.status === 'ready' &&
                              dimensionValues.data.items.map((v) => (
                                <option key={v.dimensionValueId} value={v.dimensionValueId}>
                                  {v.code} — {v.name}
                                </option>
                              ))}
                          </select>
                        )}
                      </td>
                      <td>
                        <input
                          className="sk-input"
                          value={line.description}
                          onChange={(e) => updateLine(i, { description: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="sk-input sk-num"
                          dir="ltr"
                          inputMode="numeric"
                          style={{ textAlign: 'left' }}
                          value={line.debitRial}
                          onChange={(e) =>
                            updateLine(i, { debitRial: parseRialInput(e.target.value) })
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="sk-input sk-num"
                          dir="ltr"
                          inputMode="numeric"
                          style={{ textAlign: 'left' }}
                          value={line.creditRial}
                          onChange={(e) =>
                            updateLine(i, { creditRial: parseRialInput(e.target.value) })
                          }
                        />
                      </td>
                      <td>
                        {lines.length > 2 ? (
                          <button
                            type="button"
                            className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
                            style={{ color: 'var(--sk-danger)' }}
                            onClick={() => setLines((prev) => prev.filter((_, idx) => idx !== i))}
                          >
                            <Icon name="x" size="sm" />
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} style={{ fontWeight: 600 }}>
                      مجموع
                    </td>
                    <td className="sk-num" style={{ fontWeight: 600 }}>
                      {totals.debit.toLocaleString('fa-IR')}
                    </td>
                    <td className="sk-num" style={{ fontWeight: 600 }}>
                      {totals.credit.toLocaleString('fa-IR')}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <V2StatusStrip
              tone={totals.balanced ? 'success' : 'danger'}
              title={totals.balanced ? 'سند تراز است' : 'سند تراز نیست'}
              description={
                !totals.balanced ? 'تا رفع اختلاف، ثبت پیش‌نویس امکان‌پذیر نیست.' : undefined
              }
              valueLabel={!totals.balanced ? 'اختلاف (ریال)' : undefined}
              value={
                !totals.balanced
                  ? (totals.diff < 0n ? -totals.diff : totals.diff).toLocaleString('fa-IR')
                  : undefined
              }
            />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '14px 20px',
                borderTop: '1px solid var(--sk-divider)',
                background: 'var(--sk-bg-canvas)',
              }}
            >
              <V2Button type="submit" variant="primary" disabled={!totals.balanced || submitting}>
                {submitting ? 'در حال ثبت…' : 'ثبت پیش‌نویس سند'}
              </V2Button>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginInlineStart: 'auto',
                  flexWrap: 'wrap',
                }}
              >
                <input
                  className="sk-input ltr"
                  aria-label="کد الگو"
                  placeholder="کد الگو"
                  style={{ width: 100 }}
                  value={templateCode}
                  onChange={(e) => setTemplateCode(e.target.value)}
                />
                <input
                  className="sk-input"
                  aria-label="نام الگو"
                  placeholder="نام الگو"
                  style={{ width: 160 }}
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                />
                <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={templateKeepAmounts}
                    onChange={(e) => setTemplateKeepAmounts(e.target.checked)}
                  />
                  با مبالغ
                </label>
                <V2Button type="button" variant="secondary" size="sm" onClick={saveAsTemplate}>
                  ذخیره به‌عنوان الگو
                </V2Button>
              </div>
            </div>
            {templateMessage ? (
              <div style={{ margin: '0 20px 12px', fontSize: 13, color: 'var(--sk-fg-muted)' }}>
                {templateMessage}
              </div>
            ) : null}
            {formError ? (
              <div
                style={{
                  margin: '0 20px 16px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '13px 16px',
                  borderRadius: 'var(--sk-r-md)',
                  background: 'var(--sk-danger-soft)',
                  border: '1px solid color-mix(in oklch, var(--sk-danger), transparent 78%)',
                }}
              >
                <span style={{ color: 'var(--sk-danger)', display: 'flex' }}>
                  <Icon name="x" size="sm" />
                </span>
                <span style={{ fontSize: 13, color: 'var(--sk-fg)' }}>{formError}</span>
              </div>
            ) : null}
          </form>
        </V2Card>
      ) : null}

      {journals.status === 'loading' ? <LoadingState /> : null}
      {journals.status === 'error' ? (
        <StatePanel
          variant={journals.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={journals.error.status === 403 ? 'دسترسی ندارید' : 'فهرست قابل نمایش نیست'}
        />
      ) : null}

      {journals.status === 'ready' ? (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 300px', minWidth: 0 }}>
              <V2Card>
                <V2CardHeader
                  icon="invoice"
                  title="اسناد حسابداری"
                  subtitle={`${filteredRows.length.toLocaleString('fa-IR')} سند · برای دیدن ردیف‌ها، سند را انتخاب کنید`}
                  actions={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <V2Button
                        type="button"
                        variant="primary"
                        size="sm"
                        icon="plus"
                        onClick={() => setFormOpen((open) => !open)}
                      >
                        سند جدید
                      </V2Button>
                    </div>
                  }
                />
                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    padding: '8px 14px',
                    borderBottom: '1px solid var(--sk-divider)',
                    overflowX: 'auto',
                  }}
                >
                  {(['all', 'draft', 'submitted', 'approved', 'posted'] as const).map(
                    (statusTab) => (
                      <button
                        key={statusTab}
                        type="button"
                        onClick={() => setStatusFilter(statusTab)}
                        style={{
                          padding: '4px 10px',
                          borderRadius: 16,
                          border: '1px solid',
                          borderColor:
                            statusFilter === statusTab
                              ? 'var(--sk-brand-500, #0284c7)'
                              : 'var(--sk-divider, #e5e7eb)',
                          background:
                            statusFilter === statusTab
                              ? 'var(--sk-brand-soft, #e0f2fe)'
                              : 'transparent',
                          color:
                            statusFilter === statusTab
                              ? 'var(--sk-brand-700, #0369a1)'
                              : 'var(--sk-fg-muted, #6b7280)',
                          fontSize: 11.5,
                          fontWeight: statusFilter === statusTab ? 700 : 500,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {statusTab === 'all'
                          ? 'همه اسناد'
                          : ((JOURNAL_STATUS_TEXT as Record<string, string>)[statusTab] ??
                            statusTab)}
                      </button>
                    ),
                  )}
                </div>
                {statusFilter === 'submitted' && (
                  <div
                    style={{
                      padding: '6px 14px',
                      backgroundColor: 'var(--sk-warn-soft, #fef3c7)',
                      color: 'var(--sk-warn, #b45309)',
                      fontSize: '11px',
                      borderBottom: '1px solid var(--sk-divider)',
                    }}
                  >
                    تفکیک Maker-Checker: تأیید و ثبت نهایی اسناد ارسال‌شده باید توسط کاربر دیگری غیر
                    از ثبت‌کننده اولیه انجام پذیرد.
                  </div>
                )}
                <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--sk-divider)' }}>
                  <input
                    className="sk-input"
                    aria-label="جست‌وجوی سند"
                    placeholder="شمارهٔ سند یا شرح"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
                {filteredRows.length === 0 ? (
                  <V2EmptyState icon="info" title="سندی ثبت نشده" />
                ) : (
                  <div style={{ maxHeight: 620, overflowY: 'auto' }}>
                    {filteredRows.map((row) => {
                      const active = row.journalId === activeId;
                      return (
                        <button
                          key={row.journalId}
                          type="button"
                          aria-pressed={active}
                          onClick={() => setSelectedId(row.journalId)}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                            width: '100%',
                            padding: '10px 14px',
                            textAlign: 'start',
                            font: 'inherit',
                            color: 'inherit',
                            cursor: 'pointer',
                            border: 0,
                            borderBottom: '1px solid var(--sk-divider)',
                            borderInlineStart: active
                              ? '3px solid var(--sk-brand-500)'
                              : '3px solid transparent',
                            background: active ? 'var(--sk-bg-sunken)' : 'transparent',
                          }}
                        >
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 8,
                            }}
                          >
                            <span
                              className="ltr sk-num"
                              style={{ fontSize: 13, fontWeight: active ? 700 : 600 }}
                            >
                              {row.officialDocumentNumber ?? row.provisionalNumber}
                            </span>
                            <JournalStatusBadge status={row.status} />
                          </span>
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 8,
                              fontSize: 12,
                              color: 'var(--sk-fg-muted)',
                            }}
                          >
                            <span style={{ minWidth: 0 }}>{row.description ?? '—'}</span>
                            <span
                              className="sk-num"
                              style={{
                                fontWeight: 700,
                                color: 'var(--sk-fg)',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatRialFa(row.debitTotal, unit)}
                            </span>
                          </span>
                          <span
                            className="sk-num"
                            style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}
                          >
                            {displayJalaliDate(row.businessDate)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    padding: '9px 14px',
                    fontSize: 12,
                    fontWeight: 600,
                    background: 'var(--sk-bg-canvas)',
                  }}
                >
                  <span>مجموع بدهکار · مبالغ به {label}</span>
                  <span className="sk-num">
                    {formatRialFa(
                      filteredRows.reduce((s, r) => s + BigInt(r.debitTotal), 0n).toString(),
                      unit,
                    )}
                  </span>
                </div>
              </V2Card>
            </div>

            <div
              style={{
                flex: '2.3 1 560px',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              {activeId ? (
                <JournalDocumentCard
                  key={activeId}
                  base={base}
                  journalId={activeId}
                  embedded
                  onMutated={() => setRefreshKey((k) => k + 1)}
                />
              ) : (
                <V2Card>
                  <V2EmptyState icon="info" title="سندی برای نمایش انتخاب نشده" />
                </V2Card>
              )}
            </div>
          </div>

          <V2Card>
            <V2CardHeader icon="trend" title="چرخهٔ وضعیت سند" />
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 10,
                padding: '14px 14px 10px',
              }}
            >
              {(['draft', 'submitted', 'approved', 'posted'] as const).map((s, i) => (
                <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  {i > 0 ? <span style={{ color: 'var(--sk-fg-subtle)' }}>›</span> : null}
                  <V2Badge tone={JOURNAL_STATUS_TONE[s]}>{JOURNAL_STATUS_TEXT[s]}</V2Badge>
                </span>
              ))}
              <span style={{ color: 'var(--sk-fg-subtle)' }}>⋯</span>
              <V2Badge tone="neutral">برگشت‌خورده</V2Badge>
            </div>
            <div
              style={{
                fontSize: 12.5,
                lineHeight: 1.8,
                color: 'var(--sk-fg-muted)',
                padding: '8px 14px 12px',
                borderTop: '1px dashed var(--sk-divider)',
              }}
            >
              سند «ثبت قطعی» تغییرناپذیر است؛ اصلاح فقط با سند برگشتی انجام می‌شود.
            </div>
          </V2Card>
        </>
      ) : null}
    </div>
  );
}
